import React, { useMemo, useEffect } from 'react';
import { AllocationWidget } from './AllocationWidget';
import { useQuery } from '@apollo/client';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
  selectedSectorFilterAtom,
} from '~/atoms/dashboardAtom';
import { GET_PORTFOLIO_DATA } from '~/graphql/queries/portfolio';

interface PortfolioData {
  EQPortfolioAllocation: {
    isin: string;
    current_percent: number;
    proposed_percent: number;
    change_type: 'increase' | 'decrease' | 'maintain';
    EQInstrument: {
      full_ticker: string;
      name: string;
      sector: string;
    };
  }[];
  EQPortfolioSnapshot: {
    aum: number;
    total_positions: number;
  }[];
  EQPortfoliosAIAnalysis: {
    analysis_data: {
      recommendations: {
        action: string;
        ticker: string;
        current_allocation: number;
        recommended_allocation: number;
        reason: string;
        confidence: string;
      }[];
    };
  }[];
}

export interface AllocationWidgetWrapperProps {
  widgetId: string;
  handleRemoveWidget?: (widgetId: string) => void;
  shortDescription?: string;
}

export const AllocationWidgetWrapper: React.FC<
  AllocationWidgetWrapperProps
> = ({ widgetId, handleRemoveWidget, shortDescription }) => {
  const latestSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);
  const setSelectedSectorFilter = useSetAtom(selectedSectorFilterAtom);

  const { data, loading, error, refetch } = useQuery<PortfolioData>(
    GET_PORTFOLIO_DATA,
    {
      variables: { snapshotId: latestSnapshotId },
      notifyOnNetworkStatusChange: true,
      skip: !latestSnapshotId,
    },
  );

  // Automatically refetch when snapshotId changes
  useEffect(() => {
    if (latestSnapshotId) {
      refetch();
    }
  }, [latestSnapshotId, refetch]);

  // Force hard refetch when triggered by other widgets
  useEffect(() => {
    if (triggerHardRefetch > 0) {
      refetch();
    }
  }, [triggerHardRefetch, refetch]);

  const allocationData = useMemo(() => {
    if (loading || !data?.EQPortfolioAllocation) return [];

    const mappedData = data.EQPortfolioAllocation.map(
      (item: PortfolioData['EQPortfolioAllocation'][0]) => ({
        full_ticker: item.EQInstrument.full_ticker,
        name: item.EQInstrument.name,
        currentAllocation: item.current_percent,
        proposedAllocation: item.proposed_percent,
        sector: item.EQInstrument.sector,
        changeType: item.change_type,
      }),
    );

    return mappedData
      .filter(
        (item) => item.currentAllocation !== 0 || item.proposedAllocation !== 0,
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, loading]);

  // Extract unique sectors from allocation data and update the filter atom
  useEffect(() => {
    if (allocationData.length > 0) {
      const sectors = [
        ...new Set(allocationData.map((item) => item.sector).filter(Boolean)),
      ];
      setSelectedSectorFilter(sectors);
    }
  }, [allocationData, setSelectedSectorFilter]);

  const portfolioSummary = useMemo(() => {
    if (!data?.EQPortfolioSnapshot?.[0]) return null;

    return {
      aum: data.EQPortfolioSnapshot[0].aum,
      totalPositions: allocationData.length,
    };
  }, [data, allocationData]);

  // Extract AI recommendations data
  const aiRecommendations = useMemo(() => {
    if (!data?.EQPortfoliosAIAnalysis?.[0]?.analysis_data?.recommendations)
      return [];

    return data.EQPortfoliosAIAnalysis[0].analysis_data.recommendations.map(
      (rec) => ({
        action: rec.action,
        full_ticker: rec.ticker,
        currentAllocation: `${rec.current_allocation}%`,
        proposedAllocation: `${rec.recommended_allocation}%`,
        reason: rec.reason,
        confidence: (rec.confidence.charAt(0).toUpperCase() +
          rec.confidence.slice(1)) as 'High' | 'Medium' | 'Low',
      }),
    );
  }, [data]);

  return (
    <AllocationWidget
      data={allocationData}
      isLoading={loading && !!latestSnapshotId}
      error={error || null}
      refetch={refetch}
      widgetId={widgetId}
      handleRemoveWidget={handleRemoveWidget}
      title={shortDescription}
      portfolioSummary={portfolioSummary || { aum: 0, totalPositions: 0 }}
      snapshotId={latestSnapshotId || ''}
      aiRecommendations={aiRecommendations}
    />
  );
};
